import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, FormSpy } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import { loadDraft, saveDraft, clearDraft } from 'common/utils/editorDraft';
import NewsFields from 'components/news/NewsFields';
import { ContentPreviewAction, previewImageUrl } from 'components/shared/content/ContentPreviewDialog';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultNewsFormValidations from 'common/validations/form/defaultNews';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { createNewsInitialValues } from 'common/form/initial-values/create';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import newsSlice from 'store/modules/news';

const requiredFields = ['created_at', 'body', 'title'];
// Borrador de Noticias: persiste el texto (NO la imagen ni la fecha) para no perder lo escrito.
const DRAFT_KEY = 'otx_draft_news_new';
const draftFields = ({ title, summary, body, language }) => ({ title, summary, body, language });

function NewsCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('news.create', { ns: 'routes' });
  const formRef = useRef(null);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const status = useSelector((state) => state.news.status.general);

  const initialValues = useMemo(() => {
    const draft = loadDraft(DRAFT_KEY);
    return draft ? { ...createNewsInitialValues, ...draft } : createNewsInitialValues;
  }, []);

  useEffect(() => {
    if (status === requestStatus.success && formRef.current) {
      clearDraft(DRAFT_KEY);
      formRef.current.restart();
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onCreate = useCallback(
    (values) => {
      const dataToSave = getDataToSave(values, 'news');
      dispatch(newsSlice.actions.create(dataToSave));
    },
    [dispatch]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        onSubmit={onCreate}
        validate={defaultNewsFormValidations}
        render={({ handleSubmit, form, invalid, pristine, values }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <FormSpy subscription={{ values: true }} onChange={({ values: v }) => saveDraft(DRAFT_KEY, draftFields(v))} />
              <NewsFields requiredFields={requiredFields} />
              <StyledButtonsContainer>
                <ContentPreviewAction
                  title={values.title}
                  date={values.created_at}
                  imageUrl={previewImageUrl(values.featured_image)}
                  body={values.body}
                />
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listNews.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('news.create', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </form>
          );
        }}
      />
    </>
  );
}

export default NewsCreate;
